import { captureException, type EnrichedError } from "@web-monorepo/telemetry";

export type UndocumentedSuperagentRequest = {
  method?: string;
  url?: string;
  body: unknown;
  path?: string;
  host?: string;
  protocol?: string;
};

/**
 * Minimal response shape covering only the fields our error handling actually reads. Owning it
 * here (rather than importing superagent's `Response`) means non-superagent callers — e.g. the
 * fetch-based `@web-monorepo/fetchers` layer — can build a plain object and pass it without an
 * `as unknown as Response` cast, while superagent's `Response` stays structurally assignable to it
 * for existing callers. `body` is `any` to mirror superagent's `Response["body"]` so consumers
 * reading `error.response.body` keep working.
 */
export type ApiResponseLike = {
  req?: Partial<UndocumentedSuperagentRequest>;
  text: string;
  status: number;
  statusCode: number;
  headers: Record<string, string>;
  // Optional superagent-`Response` fields some consumers still read (content-type / error).
  type?: string;
  error?: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped response JSON; mirrors superagent Response["body"]
  body?: any;
};
export function isSuccessStatus(status: number) {
  return status < 300 && status >= 200;
}

// 502-504 are usually related to a server being down, and
// will often work after a retry.  IE9 changes some of these
// codes to 12007 or similar for some reason.
export function isApiDownStatus(status: number) {
  return (status >= 502 && status <= 504) || status > 12000;
}

function parseMessage(text: string) {
  try {
    const body = JSON.parse(text) as { error?: { message?: string; detail?: unknown } };
    const message = body?.error?.message;
    let detail = body?.error?.detail;
    if (!detail && !message) return text;
    if (detail == null) return message;
    if (typeof detail === "object") detail = JSON.stringify(detail);
    if (detail === "{}") return text;
    return detail;
    // eslint-disable-next-line no-catch-all/no-catch-all
  } catch {
    return `MALFORMED ERROR RESPONSE: ${text}`;
  }
}

function getURLPath(url: string | undefined): string | undefined {
  if (!url) return undefined;

  try {
    return new URL(url, "http://dummy.com").pathname;
  } catch (x) {
    if (!(x instanceof Error)) throw x;
    // For some reason, Chrome on Windows sometimes throws "Invalid base URL":
    // https://sentry.internal.classdojo-ops.com/organizations/classdojo/issues/308640/?project=7
    captureException(x, { extra: { url } });
    return undefined;
  }
}

const getPath = (ctx: ApiResponseLike) => {
  if ("req" in ctx) {
    // In node (aka vitest), these have a req.path.
    // In browser, they have no path, they ahve URL (which is actually a path), so we need a relative URL to allow both formats:
    return ctx.req?.path ?? getURLPath(ctx.req?.url);
  }

  return;
};

const getURL = (ctx: ApiResponseLike) => {
  if ("req" in ctx) {
    if (ctx.req?.url) return ctx.req.url;
    if (ctx.req?.path && ctx.req?.host && ctx.req?.protocol) {
      return `${ctx.req.protocol}//${ctx.req.host}${ctx.req.path}`;
    }
  }
  return undefined;
};

const getMethod = (ctx: ApiResponseLike) => ("req" in ctx ? ctx?.req?.method : undefined);
const getBody = (ctx: ApiResponseLike) => ("req" in ctx ? ctx?.req?.body : undefined);

const HEADERS_ALLOWLIST = [
  "x-dojo-request-id",
  "x-cache",
  "content-type",
  "content-length",
  "content-encoding",
  "via",
  "x-backend-group",
  "x-rate-limit",
  "x-response-time",
];

function safeHeaders(headers: ApiResponseLike["headers"] | undefined): Record<string, string> {
  if (!headers) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(headers).filter(([key]) => {
      if (!key) {
        return false;
      }
      return HEADERS_ALLOWLIST.includes(key.toLowerCase());
    }),
  );
}

export class APIResponseError extends Error implements EnrichedError {
  response: ApiResponseLike;
  request: UndocumentedSuperagentRequest;
  isExpected: boolean;
  requestId?: string;
  contexts?: Record<string, Record<string, unknown>>;

  constructor(response: ApiResponseLike) {
    const path = getPath(response);
    const url = getURL(response);
    const method = getMethod(response);
    const body = getBody(response);
    const responseMessage = parseMessage(response.text);
    const message = `Error returned in API call: ${response.status} ${method} ${url} – ${responseMessage}`;

    super(message);

    // Calculate safe body length from request body
    const bodyLength = body ? (typeof body === "string" ? body.length : JSON.stringify(body).length) : 0;

    this.contexts = {
      request: {
        path,
        url,
        method,
      },
      // The name of this property is to avoid confusion with the response property, which is the superagent response object.
      classdojoResponse: {
        body:
          typeof body === "object"
            ? {
                type: typeof body,
                keys: body ? Object.keys(body) : "null",
                length: bodyLength,
              }
            : { type: typeof body, length: bodyLength },
        message: responseMessage,
        headers: safeHeaders(response.headers),
        status: response.status,
      },
    };

    this.response = response;
    this.request = {
      path,
      url,
      method,
      body,
    };
    this.isExpected = false;
    this.requestId = response.headers?.["x-dojo-request-id"];
  }
}

export function isApiResponseError(error: unknown): error is APIResponseError {
  return !!(
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "status" in error.response
  );
}
export function isExpectedApiResponseError(error: unknown): boolean {
  return !!(error && typeof error === "object" && "isExpectedError" in error && error.isExpectedError);
}
